// public/scripts/index-auth.js

// --- Imports ---
import { 
    getFirebaseAuthService, 
    getFirestoreService, 
    signInWithGoogle, // Use this from krida-auth
    signOutUser,      // Use this from krida-auth
    // krida-auth-state-changed event is used for state changes
} from './krida-auth.js';
// REMOVED: import { GoogleAuthProvider, signInWithRedirect, getRedirectResult } from "https://www.gstatic.com/firebasejs/10.12.2/firebase-auth.js";
import { doc, getDoc, setDoc, serverTimestamp } from "https://www.gstatic.com/firebasejs/10.12.2/firebase-firestore.js";
import { updatePlayerStatsUI, updateLeaderboardUI, clearGamesContent, openModal as openUiModal, closeModal as closeUiModal, handleActionFromUrl } from './index-ui.js';
import { displayMyGames } from './my-games-client.js'; // Import displayMyGames from its source
import { getFirstName } from './index-main.js'; // Import helper from main
import { getCanonicalEmail } from './krida-patra-helpers.js';
import { ThemeManager, syncThemesFromFirestore } from './theme-manager.js';
import { refreshThemeSelectorUI } from './nav-drawer-manager.js';
import { setAnalyticsUserProperties, getProgressionConfigVariant, getConfigVariantName } from './analytics.js';

// --- DOM Element References (assigned in initializeAuthEventListeners) ---
let profileIconContainer, loggedOutIcon, profileInitials, profilePicture, sidebarContent, gamesContent;

// --- Flag for initial auth check ---
let initialAuthCheckDone = false;

// --- Function to assign auth-related DOM elements ---
function assignAuthElements() {
    profileIconContainer = document.getElementById('profile-icon-container');
    loggedOutIcon = document.getElementById('logged-out-icon');
    profileInitials = document.getElementById('profile-initials');
    profilePicture = document.getElementById('profile-picture');
    sidebarContent = document.querySelector('#sidebar-content');
    gamesContent = document.getElementById('games-content');
}

// --- Sign-In Logic (using signInWithGoogle from krida-auth.js) ---
const handleSignInClick = async () => {
    try {
        // signInWithGoogle from krida-auth.js handles the popup
        await signInWithGoogle(); 
        // The 'krida-auth-state-changed' event will handle UI updates post-signin.
    } catch (error) {
        console.error("Sign-in attempt via krida-auth failed (index-auth.js):", error);
        // krida-auth.js should ideally handle user-facing error messages for sign-in.
        // If not, an alert can be added here as a fallback.
        // alert(`Sign-in error: ${error.message}`); 
    }
};

// --- Sign-Out Logic (called after confirmation, uses signOutUser from krida-auth.js) ---
export const handleSignOut = async () => {
    try {
        await signOutUser(); 
        console.log("Sign-out successful (index-auth.js via krida-auth.js).");
    } catch (error) {
        console.error("Sign-out error (index-auth.js via krida-auth.js):", error);
        alert(`Sign-out error: ${error.message}`); // Keep alert for sign-out errors here
    } finally {
        closeUiModal('logout-confirm-modal');
    }
};

// --- Auth State Change Listener Setup ---
function setupAuthStateListener() {
    document.addEventListener('krida-auth-state-changed', async (event) => {
        const user = event.detail.user;
        // const auth = getFirebaseAuthService(); // Not strictly needed here if only reading state
        const db = getFirestoreService(); // Still need for Firestore operations like setting displayName

        if (!profileIconContainer) assignAuthElements();

        if (profileIconContainer) {
            const newProfileIconContainer = profileIconContainer.cloneNode(true);
            profileIconContainer.parentNode.replaceChild(newProfileIconContainer, profileIconContainer);
            profileIconContainer = newProfileIconContainer;
            loggedOutIcon = document.getElementById('logged-out-icon');
            profileInitials = document.getElementById('profile-initials');
            profilePicture = document.getElementById('profile-picture');
        }

        if (user) {
            console.log("User signed in (index-auth.js handling event):", user);

            if (db) { 
                try {
                    const playerDocRef = doc(db, 'players', user.uid);
                    const docSnap = await getDoc(playerDocRef);
                    let playerData = docSnap.exists() ? docSnap.data() : null;
                    
                    const updates = {};
                    let needsUpdate = false;

                    if (!playerData) {
                        const firstName = getFirstName(user.displayName);
                        updates.displayName = user.displayName || firstName;
                        updates.email = user.email;
                        updates.canonicalEmail = getCanonicalEmail(user.email);
                        updates.createdAt = serverTimestamp();
                        updates.themes = {
                            unlocked: ['beach'],
                            active: 'beach'
                        };
                        updates.wins = 0;
                        updates.losses = 0;
                        updates.gamesPlayed = 0;
                        updates.tutorialCompleted = false;
                        needsUpdate = true;
                        console.info(`Player ${user.uid}: New user setup.`);
                        playerData = { ...updates }; // Use this new data locally
                        delete playerData.createdAt;
                    } else {
                        const firstName = getFirstName(user.displayName);
                        const userEmail = user.email; 
                        const canonicalUserEmail = getCanonicalEmail(userEmail);

                        if (!playerData?.displayName || playerData.displayName !== user.displayName) {
                            updates.displayName = user.displayName || firstName;
                            needsUpdate = true;
                        }
                        if (userEmail && (!playerData?.email || playerData.email !== userEmail)) {
                            updates.email = userEmail;
                            needsUpdate = true;
                        }
                        if (canonicalUserEmail && (!playerData?.canonicalEmail || playerData.canonicalEmail !== canonicalUserEmail)) {
                            updates.canonicalEmail = canonicalUserEmail;
                            needsUpdate = true;
                        }

                        // Ensure user has themes data
                        if (!playerData.themes) {
                            playerData.themes = { unlocked: ['beach'], active: 'beach' };
                            updates['themes'] = playerData.themes;
                            needsUpdate = true;
                            console.info(`Player ${user.uid}: Missing themes data, setting defaults.`);
                        } else {
                            // Ensure base themes are always unlocked
                            const baseThemes = ['beach'];
                            const currentUnlocked = playerData.themes.unlocked || [];
                            const missingBaseThemes = baseThemes.filter(t => !currentUnlocked.includes(t));

                            if (missingBaseThemes.length > 0) {
                                playerData.themes.unlocked = [...new Set([...currentUnlocked, ...baseThemes])];
                                updates['themes.unlocked'] = playerData.themes.unlocked;
                                needsUpdate = true;
                                console.info(`Player ${user.uid}: Added missing base themes: ${missingBaseThemes.join(', ')}`);
                            }

                            // Check if active theme is valid
                            if (playerData.themes.unlocked && playerData.themes.unlocked.length > 0) {
                                if (!playerData.themes.unlocked.includes(playerData.themes.active)) {
                                    const newActiveTheme = playerData.themes.unlocked[0];
                                    updates['themes.active'] = newActiveTheme;
                                    playerData.themes.active = newActiveTheme;
                                    needsUpdate = true;
                                    console.warn(`Player ${user.uid}: Active theme was invalid. Resetting to '${newActiveTheme}'.`);
                                }
                            }
                        }
                    }

                    if (needsUpdate) {
                        updates.lastLogin = serverTimestamp(); 
                        
                        console.log(`Updating player document for ${user.uid} with:`, updates);
                        await setDoc(playerDocRef, updates, { merge: true });
                        console.log("Player document updated, now syncing themes with fresh data.");
                    }
                    
                    await syncThemesFromFirestore(playerData);
                    refreshThemeSelectorUI();

                    // Auto-launch walkthrough for new users or returning users who never watched it
                    if (playerData && playerData.gamesPlayed === 0) {
                        document.dispatchEvent(new CustomEvent('krida-new-user-registered'));
                    }

                    // Set analytics user properties (best-effort)
                    try {
                        if (playerData) {
                            setAnalyticsUserProperties({
                                current_level: playerData.level || 1,
                                tier: playerData.tier || 'free',
                                games_played: playerData.gamesPlayed || 0,
                                games_won: playerData.wins || 0
                            });
                        }
                        // Initialize Remote Config and tag variant for A/B test segmentation
                        await getProgressionConfigVariant();
                        setAnalyticsUserProperties({ progression_variant: getConfigVariantName() });
                    } catch (e) {
                        // Analytics is best-effort — silently skip
                    }

                } catch (error) {
                    console.error("Error setting/updating player document:", error);
                }
            } else {
                console.warn("Firestore service (db) not available for setting player data.");
            }

            console.log("Auth state change (logged in): Updating UI components (index-auth.js)...");
            
            updatePlayerStatsUI(user.uid);
            displayMyGames(); 
            updateLeaderboardUI();
            if (sidebarContent) sidebarContent.innerHTML = '<p>Welcome! Loading content...</p>';

            // --- Use the new handler for URL actions ---
            handleActionFromUrl();
            
            initialAuthCheckDone = true; // User is logged in, so initial check is complete

        } else {
            console.log("User signed out (index-auth.js handling event).");
            if (loggedOutIcon) loggedOutIcon.style.display = 'flex';
            if (profileInitials) profileInitials.style.display = 'none';
            if (profilePicture) profilePicture.style.display = 'none';

            if (profileIconContainer) {
                profileIconContainer.title = 'Click to sign in';
                profileIconContainer.addEventListener('click', handleSignInClick);
                profileIconContainer.style.cursor = 'pointer';
            }

            updatePlayerStatsUI(null);
            // Direct DOM manipulations for simple text updates are fine here
            const rankSummary = document.getElementById('your-rank-summary');
            if (rankSummary) rankSummary.textContent = 'Rank: #--';
            const leaderboardModalContent = document.getElementById('leaderboard-modal-content');
            if (leaderboardModalContent) leaderboardModalContent.innerHTML = '<p>Please log in to see the leaderboard.</p>';
            
            updateLeaderboardUI(); // Update leaderboard (will show logged-out state)
            clearGamesContent();
            if (sidebarContent) sidebarContent.innerHTML = '<p>Log in for more options.</p>';

            // Auto-prompt for login on initial load if user is not signed in
            if (!initialAuthCheckDone) {
                console.log("Initial auth check: User not signed in. Prompting for login.");
                // Check if the current page is index.html before prompting
                if (window.location.pathname === '/' || window.location.pathname === '/index.html') {
                    signInWithGoogle().catch(err => {
                        console.error("Error during automatic sign-in attempt:", err);
                        // Optionally, display a user-friendly message if auto sign-in fails
                    });
                }
            }
            initialAuthCheckDone = true; // Mark initial check as done
        }
    });
}

// REMOVED: checkRedirectResult function as krida-auth.js uses signInWithPopup primarily.

// --- Initialize Auth Event Listeners ---
export function initializeAuthEventListeners() {
    assignAuthElements();
    setupAuthStateListener();
    // REMOVED: checkRedirectResult(); 
    console.log("index-auth.js: Initialized auth event listeners.");
} 